import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "env",
    "clientId",
    "secretId",
    "scope",
    "dropOffType",
    "serviceType",
    "zipCode",
    "savedZipCode",
    "code",
    "name",
    "address",
    "city",
    "province",
    "status",
    "results",
    "empty"
  ]

  static values = {
    url: String
  }

  connect() {
    this.syncServiceType()
  }

  syncServiceType() {
    if (!this.hasDropOffTypeTarget || !this.hasServiceTypeTarget) return

    const mapping = {
      locker: "APT",
      punto_poste: "RTZ",
      post_office: "FMP"
    }

    const nextValue = mapping[this.dropOffTypeTarget.value] || ""
    this.serviceTypeTarget.value = nextValue
  }

  search(event) {
    if (event) event.preventDefault()
    this.syncServiceType()

    const zipCode = this.zipCodeTarget.value.trim()
    const serviceType = this.serviceTypeTarget.value.trim()

    if (zipCode.length !== 5 || !serviceType) {
      this.renderStatus("Inserisci un CAP valido e seleziona una modalità compatibile.", "warning")
      this.clearResults()
      return
    }

    this.renderStatus("Ricerca in corso...", "info")
    this.clearResults()

    fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({
        pdb_env: this.envTarget.value,
        pdb_client_id: this.clientIdTarget.value,
        pdb_secret_id: this.secretIdTarget.value,
        pdb_scope: this.scopeTarget.value,
        zip_code: zipCode,
        service_type: serviceType
      })
    })
      .then(async (response) => {
        const payload = await response.json()
        if (!response.ok || !payload.success) {
          throw new Error(payload.error || "Ricerca non riuscita")
        }

        return payload.data
      })
      .then((data) => this.renderResults(data.points || []))
      .catch((error) => {
        this.renderStatus(error.message, "danger")
        this.clearResults()
      })
  }

  select(event) {
    const { code, name, address, city, province, zipCode } = event.currentTarget.dataset

    this.codeTarget.value = code || ""
    this.nameTarget.value = name || ""
    this.addressTarget.value = address || ""
    this.cityTarget.value = city || ""
    this.provinceTarget.value = province || ""
    this.zipCodeTarget.value = zipCode || this.zipCodeTarget.value
    if (this.hasSavedZipCodeTarget) this.savedZipCodeTarget.value = zipCode || this.savedZipCodeTarget.value

    this.renderStatus(`Selezionato: ${name || code}`, "success")
    this.highlightSelected(event.currentTarget)
  }

  renderResults(points) {
    this.clearResults()

    if (points.length === 0) {
      this.renderStatus("Nessun punto trovato per questo CAP.", "warning")
      this.emptyTarget.classList.remove("d-none")
      return
    }

    this.renderStatus(`${points.length} punti trovati. Selezionane uno per compilare il form.`, "success")

    points.forEach((point) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "list-group-item list-group-item-action"
      button.dataset.action = "click->delivery-points#select"
      button.dataset.code = point.code || ""
      button.dataset.name = point.name || ""
      button.dataset.address = point.address || ""
      button.dataset.city = point.city || ""
      button.dataset.province = point.province || ""
      button.dataset.zipCode = point.zip_code || ""

      const hours = this.buildHours(point)
      button.innerHTML = `
        <div class="fw-semibold">${this.escapeHtml(point.name || point.code || "Punto Poste")}</div>
        <div class="small text-muted">${this.escapeHtml([point.address, point.zip_code, point.city, point.province].filter(Boolean).join(", "))}</div>
        <div class="small text-muted">${this.escapeHtml(hours)}</div>
      `

      this.resultsTarget.appendChild(button)
    })
  }

  clearResults() {
    this.resultsTarget.innerHTML = ""
    this.emptyTarget.classList.add("d-none")
  }

  renderStatus(message, tone) {
    this.statusTarget.className = `alert alert-${tone} py-2 mb-3`
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("d-none")
  }

  highlightSelected(element) {
    this.resultsTarget.querySelectorAll(".list-group-item").forEach((item) => {
      item.classList.remove("active")
    })
    element.classList.add("active")
  }

  buildHours(point) {
    const mon = point.open_time_mon && point.close_time_mon ? `Lun-Ven ${point.open_time_mon}-${point.close_time_mon}` : null
    const sat = point.open_time_sat && point.close_time_sat ? `Sab ${point.open_time_sat}-${point.close_time_sat}` : null
    return [mon, sat].filter(Boolean).join(" · ") || "Orari non disponibili"
  }

  csrfToken() {
    const token = document.querySelector("meta[name='csrf-token']")
    return token ? token.content : ""
  }

  escapeHtml(value) {
    return value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
